import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useAuth } from '@/contexts/AuthContext'
import {
  useDataRequests,
  useProcessDataRequest,
  useCompleteDataRequest,
  useGenerateClientExport,
  useExportToFormat,
  useComplianceStats,
} from '@/hooks/useRGPD'
import { formatDate } from '@/lib/utils'
import {
  dataRequestTypeLabels,
  dataRequestStatusLabels,
  dataRequestStatusColors,
} from '@/types/rgpd'
import type {
  DataRequest,
  DataRequestType,
  DataRequestStatus,
  ExportFormat,
} from '@/types/rgpd'
import {
  Search,
  Download,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  MoreHorizontal,
  User,
  Mail,
  Calendar,
  ArrowRight,
  FileJson,
  FileSpreadsheet,
  FileIcon,
  RefreshCw,
  Shield,
  Filter,
  Plus,
  AlertTriangle,
} from 'lucide-react'

// STORY-048: Page des demandes de donnees

const typeFilters: { value: DataRequestType | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous les types' },
  { value: 'export', label: 'Export de donnees' },
  { value: 'deletion', label: 'Suppression' },
  { value: 'rectification', label: 'Rectification' },
  { value: 'portability', label: 'Portabilite' },
]

const statusFilters: { value: DataRequestStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'completed', label: 'Terminee' },
  { value: 'rejected', label: 'Rejetee' },
]

const formatIcons: Record<ExportFormat, React.ReactNode> = {
  json: <FileJson className="w-4 h-4" />,
  pdf: <FileIcon className="w-4 h-4" />,
  csv: <FileSpreadsheet className="w-4 h-4" />,
}

export default function DataRequestsPage() {
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<DataRequestType | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<DataRequestStatus | 'all'>('pending')
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<DataRequest | null>(null)
  const [showProcessModal, setShowProcessModal] = useState(false)
  const [processAction, setProcessAction] = useState<'approve' | 'reject' | null>(null)
  const [processNotes, setProcessNotes] = useState('')
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFormat, setExportFormat] = useState<ExportFormat>('json')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const filters = useMemo(() => ({
    search: search || undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  }), [search, typeFilter, statusFilter])

  const { data: requests, isLoading, refetch } = useDataRequests(filters)
  const { data: stats } = useComplianceStats()

  const processRequest = useProcessDataRequest()
  const completeRequest = useCompleteDataRequest()
  const generateExport = useGenerateClientExport()
  const exportToFormat = useExportToFormat()

  const openDetail = (request: DataRequest) => {
    setSelectedRequest(request)
    setShowDetailModal(true)
    setShowActionsMenu(null)
  }

  const openProcessModal = (request: DataRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request)
    setProcessAction(action)
    setProcessNotes('')
    setShowProcessModal(true)
    setShowActionsMenu(null)
  }

  const handleProcess = async () => {
    if (!selectedRequest || !processAction || !user) return

    try {
      if (processAction === 'approve') {
        await processRequest.mutateAsync({
          id: selectedRequest.id,
          action: 'approve',
          adminId: user.id,
          notes: processNotes,
        })

        // Si c'est une demande d'export, generer l'export
        if (selectedRequest.type === 'export') {
          setShowProcessModal(false)
          setShowExportModal(true)
        } else if (selectedRequest.type === 'deletion') {
          setShowProcessModal(false)
          setShowDeleteConfirm(true)
        } else {
          setShowProcessModal(false)
          setSelectedRequest(null)
        }
      } else {
        await processRequest.mutateAsync({
          id: selectedRequest.id,
          action: 'reject',
          adminId: user.id,
          notes: processNotes,
        })
        setShowProcessModal(false)
        setSelectedRequest(null)
      }
    } catch (error) {
      console.error('Erreur lors du traitement:', error)
    }
  }

  const handleGenerateExport = async () => {
    if (!selectedRequest || !user) return

    try {
      const clientData = await generateExport.mutateAsync(selectedRequest.client_id)
      const exportedData = await exportToFormat.mutateAsync({
        data: clientData,
        format: exportFormat,
      })

      // Telecharger le fichier
      const mimeTypes: Record<ExportFormat, string> = {
        json: 'application/json',
        pdf: 'application/pdf',
        csv: 'text/csv',
      }
      const blob = new Blob([exportedData], { type: mimeTypes[exportFormat] })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `donnees-${selectedRequest.client_name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.${exportFormat}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      // Marquer la demande comme terminee
      await completeRequest.mutateAsync({
        id: selectedRequest.id,
        adminId: user.id,
        result: {
          export_url: url,
          notes: `Export ${exportFormat.toUpperCase()} genere avec succes`,
        },
      })

      setShowExportModal(false)
      setSelectedRequest(null)
    } catch (error) {
      console.error('Erreur lors de l\'export:', error)
    }
  }

  const handleConfirmDeletion = async () => {
    if (!selectedRequest || !user) return

    try {
      // Simuler la suppression des donnees
      await completeRequest.mutateAsync({
        id: selectedRequest.id,
        adminId: user.id,
        result: {
          notes: 'Donnees personnelles supprimees conformement a la demande',
        },
      })

      setShowDeleteConfirm(false)
      setSelectedRequest(null)
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
    }
  }

  const getTypeIcon = (type: DataRequestType) => {
    switch (type) {
      case 'export':
        return <Download className="w-4 h-4" />
      case 'deletion':
        return <Trash2 className="w-4 h-4" />
      case 'rectification':
        return <FileText className="w-4 h-4" />
      case 'portability':
        return <ArrowRight className="w-4 h-4" />
      default:
        return <FileText className="w-4 h-4" />
    }
  }

  const getStatusIcon = (status: DataRequestStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-mint-600" />
      case 'pending':
        return <Clock className="w-4 h-4 text-gold-600" />
      case 'in_progress':
        return <RefreshCw className="w-4 h-4 text-brown-600" />
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-600" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to="/rgpd"
              className="text-brown-500 hover:text-brown-700 transition-colors"
            >
              RGPD
            </Link>
            <span className="text-brown-400">/</span>
            <span className="text-brown-900">Demandes de donnees</span>
          </div>
          <h1 className="text-2xl font-display font-bold text-brown-900 mt-2">
            Demandes de donnees
          </h1>
          <p className="mt-1 text-brown-600">
            Gerez les demandes d'export et de suppression
          </p>
        </div>
        <Button
          variant="outline"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={() => refetch()}
          isLoading={isLoading}
        >
          Actualiser
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-gold-100 flex items-center justify-center">
            <Clock className="w-6 h-6 text-gold-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">En attente</p>
            <p className="text-2xl font-bold text-brown-900">
              {stats?.pendingRequests || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-mint-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Traitees</p>
            <p className="text-2xl font-bold text-brown-900">
              {stats?.completedRequests || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
            <Download className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Exports</p>
            <p className="text-2xl font-bold text-brown-900">
              {stats?.exportRequests || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Suppressions</p>
            <p className="text-2xl font-bold text-brown-900">
              {stats?.deletionRequests || 0}
            </p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Rechercher par nom ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as DataRequestType | 'all')}
              className="w-44"
            >
              {typeFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </Select>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DataRequestStatus | 'all')}
              className="w-36"
            >
              {statusFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {/* Requests list */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : !requests || requests.length === 0 ? (
          <div className="text-center py-12 text-brown-500">
            <Shield className="w-12 h-12 mx-auto mb-3 text-beige-300" />
            <p className="font-medium">Aucune demande trouvee</p>
            <p className="text-sm mt-1">
              {search || typeFilter !== 'all' || statusFilter !== 'all'
                ? 'Essayez avec d\'autres criteres'
                : 'Aucune demande de donnees pour le moment'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Type / Client
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Date demande
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Motif
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-beige-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          request.type === 'export' ? 'bg-blue-100 text-blue-600' :
                          request.type === 'deletion' ? 'bg-red-100 text-red-600' :
                          'bg-beige-100 text-beige-600'
                        }`}>
                          {getTypeIcon(request.type)}
                        </div>
                        <div>
                          <Badge
                            variant={request.type === 'deletion' ? 'red' : 'blue'}
                            size="sm"
                          >
                            {dataRequestTypeLabels[request.type]}
                          </Badge>
                          <p className="font-medium text-brown-900 mt-1">
                            {request.client_name}
                          </p>
                          <p className="text-sm text-brown-500">
                            {request.client_email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-brown-600">
                        {formatDate(request.created_at)}
                      </div>
                      {request.processed_at && (
                        <div className="text-xs text-brown-400 mt-0.5">
                          Traite le {formatDate(request.processed_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(request.status)}
                        <Badge variant={dataRequestStatusColors[request.status].variant}>
                          {dataRequestStatusLabels[request.status]}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-brown-600 max-w-xs truncate">
                        {request.reason || '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setShowActionsMenu(showActionsMenu === request.id ? null : request.id)}
                          className="text-brown-400 hover:text-brown-600 p-1 rounded-lg hover:bg-beige-100"
                        >
                          <MoreHorizontal className="w-5 h-5" />
                        </button>

                        {showActionsMenu === request.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setShowActionsMenu(null)}
                            />
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-beige-200 z-20 py-1">
                              <button
                                onClick={() => openDetail(request)}
                                className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4" />
                                Voir les details
                              </button>
                              {request.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => openProcessModal(request, 'approve')}
                                    className="w-full px-4 py-2 text-left text-sm text-mint-600 hover:bg-mint-50 flex items-center gap-2"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Approuver
                                  </button>
                                  <button
                                    onClick={() => openProcessModal(request, 'reject')}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Rejeter
                                  </button>
                                </>
                              )}
                              {request.status === 'completed' && request.export_url && (
                                <button
                                  onClick={() => {
                                    // Telecharger l'export
                                    setShowActionsMenu(null)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2"
                                >
                                  <Download className="w-4 h-4" />
                                  Telecharger l'export
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination info */}
        {requests && requests.length > 0 && (
          <div className="px-6 py-3 border-t border-beige-200 text-sm text-brown-500">
            {requests.length} demande{requests.length > 1 ? 's' : ''}
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
          setSelectedRequest(null)
        }}
        title="Details de la demande"
        size="lg"
      >
        {selectedRequest && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                selectedRequest.type === 'export' ? 'bg-blue-100 text-blue-600' :
                selectedRequest.type === 'deletion' ? 'bg-red-100 text-red-600' :
                'bg-beige-100 text-beige-600'
              }`}>
                {getTypeIcon(selectedRequest.type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge
                    variant={selectedRequest.type === 'deletion' ? 'red' : 'blue'}
                  >
                    {dataRequestTypeLabels[selectedRequest.type]}
                  </Badge>
                  <Badge variant={dataRequestStatusColors[selectedRequest.status].variant}>
                    {dataRequestStatusLabels[selectedRequest.status]}
                  </Badge>
                </div>
                <h3 className="text-lg font-medium text-brown-900 mt-2">
                  {selectedRequest.client_name}
                </h3>
                <p className="text-brown-500">{selectedRequest.client_email}</p>
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-beige-50 rounded-lg">
                <div className="flex items-center gap-2 text-brown-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Date de demande</span>
                </div>
                <p className="font-medium text-brown-900">
                  {formatDate(selectedRequest.created_at)}
                </p>
              </div>
              {selectedRequest.format && (
                <div className="p-4 bg-beige-50 rounded-lg">
                  <div className="flex items-center gap-2 text-brown-500 mb-1">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Format demande</span>
                  </div>
                  <p className="font-medium text-brown-900 uppercase">
                    {selectedRequest.format}
                  </p>
                </div>
              )}
            </div>

            {/* Reason */}
            {selectedRequest.reason && (
              <div>
                <h4 className="text-sm font-medium text-brown-500 mb-2">Motif de la demande</h4>
                <p className="text-brown-700 bg-beige-50 p-4 rounded-lg">
                  {selectedRequest.reason}
                </p>
              </div>
            )}

            {/* Notes */}
            {selectedRequest.notes && (
              <div>
                <h4 className="text-sm font-medium text-brown-500 mb-2">Notes de traitement</h4>
                <p className="text-brown-700 bg-beige-50 p-4 rounded-lg">
                  {selectedRequest.notes}
                </p>
              </div>
            )}

            {/* Actions */}
            {selectedRequest.status === 'pending' && (
              <div className="flex gap-3 pt-4 border-t border-beige-200">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDetailModal(false)
                    openProcessModal(selectedRequest, 'reject')
                  }}
                  leftIcon={<XCircle className="w-4 h-4" />}
                >
                  Rejeter
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    setShowDetailModal(false)
                    openProcessModal(selectedRequest, 'approve')
                  }}
                  leftIcon={<CheckCircle className="w-4 h-4" />}
                >
                  Approuver
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Process Modal */}
      <Modal
        isOpen={showProcessModal}
        onClose={() => {
          setShowProcessModal(false)
          setSelectedRequest(null)
          setProcessAction(null)
          setProcessNotes('')
        }}
        title={processAction === 'approve' ? 'Approuver la demande' : 'Rejeter la demande'}
      >
        {selectedRequest && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${
              processAction === 'approve' ? 'bg-mint-50 border border-mint-200' : 'bg-red-50 border border-red-200'
            }`}>
              <p className={processAction === 'approve' ? 'text-mint-800' : 'text-red-800'}>
                Vous etes sur le point de {processAction === 'approve' ? 'approuver' : 'rejeter'} la demande de{' '}
                <span className="font-medium">{dataRequestTypeLabels[selectedRequest.type].toLowerCase()}</span>{' '}
                de <span className="font-medium">{selectedRequest.client_name}</span>.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Notes {processAction === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={processNotes}
                onChange={(e) => setProcessNotes(e.target.value)}
                className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                rows={3}
                placeholder={processAction === 'reject'
                  ? 'Indiquez le motif du rejet...'
                  : 'Notes optionnelles...'}
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowProcessModal(false)
                  setSelectedRequest(null)
                  setProcessAction(null)
                  setProcessNotes('')
                }}
              >
                Annuler
              </Button>
              <Button
                variant={processAction === 'approve' ? 'primary' : 'danger'}
                onClick={handleProcess}
                isLoading={processRequest.isPending}
                disabled={processAction === 'reject' && !processNotes.trim()}
              >
                {processAction === 'approve' ? 'Approuver' : 'Rejeter'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Export Format Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => {
          setShowExportModal(false)
          setSelectedRequest(null)
        }}
        title="Generer l'export"
      >
        {selectedRequest && (
          <div className="space-y-4">
            <p className="text-brown-600">
              Selectionnez le format d'export pour les donnees de{' '}
              <span className="font-medium text-brown-900">{selectedRequest.client_name}</span>.
            </p>

            <div className="grid grid-cols-3 gap-3">
              {(['json', 'pdf', 'csv'] as ExportFormat[]).map((format) => (
                <button
                  key={format}
                  onClick={() => setExportFormat(format)}
                  className={`p-4 rounded-lg border-2 text-center transition-colors ${
                    exportFormat === format
                      ? 'border-mint-500 bg-mint-50'
                      : 'border-beige-200 hover:border-beige-300'
                  }`}
                >
                  <div className="flex justify-center mb-2">
                    {formatIcons[format]}
                  </div>
                  <span className="text-sm font-medium uppercase">{format}</span>
                </button>
              ))}
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowExportModal(false)
                  setSelectedRequest(null)
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleGenerateExport}
                isLoading={generateExport.isPending || exportToFormat.isPending}
                leftIcon={<Download className="w-4 h-4" />}
              >
                Generer et telecharger
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setSelectedRequest(null)
        }}
        title="Confirmer la suppression"
      >
        {selectedRequest && (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">
                    Cette action est irreversible
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Toutes les donnees personnelles de {selectedRequest.client_name} seront definitivement supprimees,
                    a l'exception des documents a conserver pour des raisons legales (factures, contrats).
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-brown-700 mb-2">Donnees a supprimer:</h4>
              <ul className="space-y-1 text-sm text-brown-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-500" />
                  Informations personnelles (nom, email, telephone)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-500" />
                  Historique des communications
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-500" />
                  Documents non legaux
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-500" />
                  Preferences et consentements
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-medium text-brown-700 mb-2">Donnees conservees (obligation legale):</h4>
              <ul className="space-y-1 text-sm text-brown-600">
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-brown-400" />
                  Factures (conservation 10 ans)
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-brown-400" />
                  Contrats signes
                </li>
              </ul>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setSelectedRequest(null)
                }}
              >
                Annuler
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDeletion}
                isLoading={completeRequest.isPending}
                leftIcon={<Trash2 className="w-4 h-4" />}
              >
                Confirmer la suppression
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
